import { useEffect, useMemo, useState, useRef } from 'react'
import { isEmpty, sortBy, isNumber, isEqual, cloneDeep } from 'lodash'
import { useSelector } from 'react-redux'
import moment from 'moment/moment'

import { Box, Stack } from '@mui/material'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import { DrivePolylineComponent } from './DrivePolyineComponent'
import LoadingSpinner from '../../../../../../custom-components/LoadingSpinner'

import { defaultMapState } from '../helper/constants'

import { useDriverEmailQuery, useDrives } from './hook/index'
import DriverMapFilters from './DriverMapFilters'
import { DriverHeartBeatComponent } from './DriverHeartBeatComponent'
import { useDriverHeartBeat } from './hook/useDriverHeartBeatQuery'
import { useMarkerInfoBanner } from './hook/useMarkerInfoHook'
import { useMostRecentDriveQuery } from './hook/useMostRecentDriveQuery'

export function DriverMapInfo(props) {
	const {
		tileKey,
		fromDate,
		toDate,
		userEmail,
		showFilters,
		repeatReloadMapSeconds,
		showHistory,
		pointOfInterest,
		isRemovedTile,
		driveId,
		userIdByDriveId,
		allHeartBeatQueryRef,
		allDrivesQueryRef,
	} = props
	const environment = useSelector((state) => state.environment)
	const [activeMarker, setActiveMarker] = useState(null)
	const [mapRef, setMapRef] = useState(null)

	const { handleMouseOutChangeMarkerInfo, handleMouseOverChangeMarkerInfo, buildMarkerInfoBanner } =
		useMarkerInfoBanner()

	const driveIdRef = useRef(null)
	const fromDateRef = useRef(null)
	const endDateRef = useRef(null)

	const [center, setCenter] = useState(defaultMapState.center)
	const [zoom, setZoom] = useState(defaultMapState.zoom)
	const [stoppedLocation, setStoppedLocation] = useState(null)

	const { driverInfoByEmail, isLoadingUser } = useDriverEmailQuery({
		userEmail,
	})

	const userId = useMemo(() => {
		if (userIdByDriveId) return userIdByDriveId
		if (isLoadingUser || !userEmail || isEmpty(driverInfoByEmail)) return null

		if (!isEqual(userEmail, driverInfoByEmail.email)) return null

		return driverInfoByEmail.id
	}, [userIdByDriveId, userEmail, isLoadingUser, driverInfoByEmail])

	// Fetch drives
	const { allDrives, isLoadingDrives: isLoadingAllDrives } = useDrives({
		driveId,
		userId: userId,
		fromDate,
		toDate,
		showHistory,
		allDrivesQueryRef,
	})

	// useEffect(() => {
	// 	if (isEmpty(allDrives)) {
	// 		setUseMostRecent(false)
	// 		return
	// 	}

	// 	const lastDrive = allDrives[0]
	// 	setUseMostRecent(!Boolean(lastDrive?.end_date_timestamp))
	// }, [allDrives])

	// Driver HeartBeat
	const { allDriverHeartBeat, isLoadingDriverHeartBeat } = useDriverHeartBeat({
		userId: userId,
		fromDate,
		toDate,
		showHistory,
		allHeartBeatQueryRef,
	})

	const shouldRefetchLocations = useMemo(() => {
		if (!toDate) return false

		const startBeforeDate = moment.unix(toDate).toISOString()
		const now = moment().toISOString()

		const startBeforeMoment = moment.utc(startBeforeDate)
		const nowMoment = moment.utc(now)

		// startBeforeDate always is end of day
		const rs = startBeforeMoment.isSameOrAfter(nowMoment)
		return rs
	}, [toDate])

	const { mostRecentDrive, mostRecentHeartBeat } = useMostRecentDriveQuery({
		tileKey,
		driveId,
		useMostRecent: shouldRefetchLocations,
		userId: userId,
		fromDate,
		toDate,
		showHistory,
		repeatReloadMapSeconds,
	})

	const [displayDrives, setDisplayDrives] = useState([])
	useEffect(() => {
		if (!showHistory && !driveId) {
			const newDisplayDrives = isEmpty(mostRecentDrive) ? [] : [mostRecentDrive]
			if (isEqual(newDisplayDrives, displayDrives)) return
			setDisplayDrives(newDisplayDrives)
			return
		}

		const drives = allDrives || []
		if (!mostRecentDrive || isEmpty(mostRecentDrive)) {
			if (isEqual(drives, displayDrives)) return
			setDisplayDrives(drives)
			return
		}

		const mostRecentDriveId = mostRecentDrive.id
		const hasMostRecentDrive = drives.some((drive) => drive.id === mostRecentDriveId)
		if (hasMostRecentDrive) {
			if (isEqual(drives, displayDrives)) return
			setDisplayDrives(drives)
		} else {
			// save the previous most recent drive
			setDisplayDrives((pre) => [mostRecentDrive, ...pre])
		}
	}, [allDrives, showHistory, mostRecentDrive, driveId])

	const currentLocationDrive = useMemo(() => {
		if (showHistory || isEmpty(displayDrives)) return null

		return displayDrives[0]
	}, [showHistory, displayDrives])

	const [locationByDriveId, setLocationByDriveIds] = useState({})
	const [allHeartBeats, setAllHeartBeats] = useState([])

	useEffect(() => {
		if (!showHistory && !driveId) {
			const newDisplayHeartBeats = isEmpty(mostRecentHeartBeat) ? [] : [mostRecentHeartBeat]
			if (isEqual(newDisplayHeartBeats, allHeartBeats)) return
			setAllHeartBeats(newDisplayHeartBeats)
			return
		}

		try {
			let result = allDriverHeartBeat
			if (typeof allDriverHeartBeat === 'string') {
				result = []
			}

			if (!mostRecentHeartBeat || isEmpty(mostRecentHeartBeat)) {
				if (isEqual(result, allHeartBeats)) return
				setAllHeartBeats(result)
				return
			}

			const hasMostRecentHeartBeat = allDriverHeartBeat.some(
				(heartBeat) => heartBeat.key === mostRecentHeartBeat?.key
			)

			if (hasMostRecentHeartBeat) {
				if (isEqual(result, allHeartBeats)) return
				setAllHeartBeats(result)
			} else {
				setAllHeartBeats((pre) => [mostRecentHeartBeat, ...pre])
			}
		} catch (err) {
			console.log(err)
		}
	}, [allDriverHeartBeat, showHistory, JSON.stringify(mostRecentHeartBeat), driveId])

	const allLocations = useMemo(() => {
		if (isEmpty(locationByDriveId)) return []

		if (driveId) {
			return sortBy(locationByDriveId[driveId], (obj) => moment.utc(obj.timestamp))
		}

		return sortBy(Object.values(locationByDriveId).flat() || [], (obj) => moment.utc(obj.timestamp))
	}, [JSON.stringify(locationByDriveId), driveId]) // Store all locations

	const updateLocationsByDriveId = (id, locations) => {
		setLocationByDriveIds((prev) => {
			return { ...prev, [id]: locations }
		})
	}

	useEffect(() => {
		const preDriveId = driveIdRef.current

		if (isEqual(driveId, preDriveId)) return

		setStoppedLocation(null)
		driveIdRef.current = driveId
		if (preDriveId) {
			const has = Object.prototype.hasOwnProperty.call(locationByDriveId, preDriveId)
			if (has) {
				const newLocationByDriveId = cloneDeep(locationByDriveId)
				delete newLocationByDriveId[preDriveId]
				setLocationByDriveIds(newLocationByDriveId)
			}
		}
	}, [driveId])

	useEffect(() => {
		const preFromDate = fromDateRef.current
		const preToDate = endDateRef.current

		if (!isEqual(preFromDate, fromDate) || isEqual(!preToDate, toDate)) {
			fromDateRef.current = fromDate
			endDateRef.current = toDate
			setLocationByDriveIds({})
		}
	}, [fromDate, toDate])

	useEffect(() => {
		if (isLoadingUser || isEmpty(driverInfoByEmail)) {
			setLocationByDriveIds({})
			if (mapRef) {
				fitBounds(mapRef)
			}
		}
	}, [mapRef, isLoadingUser, driverInfoByEmail])

	function fitMostRecentBounds(
		map,
		showHistory,
		mostRecentHeartBeat,
		allHeartBeats,
		mostRecentDrive,
		locationByDriveId
	) {
		if (!map) return

		// if showHistory is false, we want to center the map on the most recent heart beat or drive
		if (!showHistory) {
			if (mostRecentHeartBeat) {
				const updatedMostRecentHeartBeat = allHeartBeats.some(
					(heartBeat) => heartBeat.key === mostRecentHeartBeat.key
				)

				if (!updatedMostRecentHeartBeat) return
				setCenter({ lat: mostRecentHeartBeat.lat, lng: mostRecentHeartBeat.lon })
				return
			} else if (mostRecentDrive) {
				const mostRecentLocations = locationByDriveId?.[mostRecentDrive?.id] || []
				if (isEmpty(mostRecentLocations)) return

				const lastLocation = mostRecentDrive.locations.at(-1)
				if (lastLocation) {
					setCenter({ lat: lastLocation.lat, lng: lastLocation.lng })
					return
				}
			}

			return
		}

		// If showHistory is true, we want to find which is the most recent location
		const mostRecentHeartBeatTimestamp = mostRecentHeartBeat?.timestamp || null
		const currentDriveTimestamp = mostRecentDrive?.end_date_timestamp || null

		let isBeforeMostRecentHeartBeat = false
		const mostRecentHeartBeatTimestampMoment = moment.utc(mostRecentHeartBeatTimestamp)
		const currentDriveTimestampMoment = moment.utc(currentDriveTimestamp)

		if (mostRecentHeartBeatTimestampMoment.isValid() && currentDriveTimestampMoment.isValid()) {
			isBeforeMostRecentHeartBeat = currentDriveTimestampMoment.isBefore(
				mostRecentHeartBeatTimestampMoment
			)
		}

		if (isBeforeMostRecentHeartBeat) {
			setCenter({ lat: mostRecentHeartBeat.lat, lng: mostRecentHeartBeat.lon })
			return
		} else if (mostRecentDrive) {
			const mostRecentLocations = locationByDriveId?.[mostRecentDrive?.id] || []
			if (isEmpty(mostRecentLocations)) return
			const lastLocation = mostRecentLocations.at(-1)
			if (lastLocation) {
				setCenter({ lat: lastLocation.lat, lng: lastLocation.lng })
				return
			}
		}
	}

	const fitBounds = (
		map,
		showHistory,
		allLocations,
		allHeartBeats,
		stoppedLocation,
		mostRecentHeartBeat
	) => {
		if (!map) return

		if (!showHistory) {
			if (stoppedLocation) {
				setCenter({ lat: stoppedLocation.lat, lng: stoppedLocation.long })
				return
			} else if (mostRecentHeartBeat) {
				setCenter({ lat: mostRecentHeartBeat.lat, lng: mostRecentHeartBeat.lon })
				return
			} else {
				setCenter(defaultMapState.center)
				setZoom(defaultMapState.zoom)

				// Force map to reapply the zoom
				map.setZoom(defaultMapState.zoom)
				return
			}
		}

		const bounds = new window.google.maps.LatLngBounds()

		if (isEmpty(allLocations)) {
			if (!isEmpty(allHeartBeats)) {
				allHeartBeats.forEach(({ lat, lon }) => bounds.extend({ lat: lat, lng: lon }))
				mapRef.fitBounds(bounds)
				const centerFromBounds = bounds.getCenter()
				if (!centerFromBounds) return
				setCenter(centerFromBounds)
				return
			}
			setCenter(defaultMapState.center)
			setZoom(defaultMapState.zoom)

			// Force map to reapply the zoom
			map.setZoom(defaultMapState.zoom)
			return
		}

		allLocations.forEach(({ lat, lng }) => bounds.extend({ lat: lat, lng: lng }))

		map.fitBounds(bounds)

		const centerFromBounds = bounds.getCenter()
		if (!centerFromBounds) return
		setCenter(centerFromBounds)
	}

	useEffect(() => {
		if (showHistory || !isEmpty(mostRecentDrive) || isEqual(stoppedLocation, null)) return

		setStoppedLocation(null)
	}, [showHistory, mostRecentDrive])

	useEffect(() => {
		if (!mapRef) return

		if (!driveId && (!isEmpty(mostRecentHeartBeat) || !isEmpty(mostRecentDrive))) {
			fitMostRecentBounds(
				mapRef,
				showHistory,
				mostRecentHeartBeat,
				allHeartBeats,
				mostRecentDrive,
				locationByDriveId
			)
			return
		}

		fitBounds(
			mapRef,
			showHistory,
			allLocations,
			allHeartBeats,
			stoppedLocation,
			mostRecentHeartBeat
		)
	}, [
		driveId,
		showHistory,
		stoppedLocation,
		allLocations,
		mapRef,
		allHeartBeats,
		mostRecentDrive,
		mostRecentHeartBeat,
		locationByDriveId,
	])

	useEffect(() => {
		if (!mapRef) return
		fitBounds(mapRef)
	}, [userEmail])

	const handleOnLoad = (map) => {
		if (!map) return

		// Store a reference to the google map instance in state
		setMapRef(map)

		// Fit map bounds to contain all markers
		fitBounds(map)
	}

	const handleActiveMarker = (marker) => {
		if (marker === activeMarker) {
			return
		}
		setActiveMarker(marker)
	}

	return (
		<Stack
			sx={{
				height: '100%',
				width: '100%',
				mt: '6px !important',
			}}
		>
			{showFilters && (
				<Stack px={1} direction="row" alignItems="center" spacing={2}>
					<DriverMapFilters
						userEmail={userEmail}
						fromDate={moment.unix(fromDate).toISOString()}
						toDate={moment.unix(toDate).toISOString()}
						disableFilters
					/>
				</Stack>
			)}

			{!isRemovedTile?.[tileKey] && (
				<Box sx={{ flex: 1, mt: 1 }}>
					<GoogleMap
						onLoad={handleOnLoad}
						center={center}
						zoom={zoom}
						onClick={() => setActiveMarker(null)}
						mapContainerStyle={{
							height: '100%',
							width: '100%',
						}}
						options={{
							minZoom: 4,
							gestureHandling: 'greedy',
							styles: [
								{
									featureType: 'poi',
									stylers: [{ visibility: pointOfInterest ? 'on' : 'off' }],
								},
							],
						}}
					>
						{isLoadingUser || isLoadingAllDrives || isLoadingDriverHeartBeat ? (
							<LoadingSpinner />
						) : (
							<>
								{displayDrives?.map((drive, index) => (
									<DrivePolylineComponent
										key={`drive-${drive.id || index}`}
										index={index}
										drive={drive}
										userId={userId}
										userEmail={userEmail}
										fromDate={fromDate}
										toDate={toDate}
										activeMarker={activeMarker}
										setActiveMarker={setActiveMarker}
										handleActiveMarker={handleActiveMarker}
										locationByDriveId={locationByDriveId}
										updateLocationsByDriveId={updateLocationsByDriveId}
										showHistory={showHistory}
										stoppedLocation={stoppedLocation}
										setStoppedLocation={setStoppedLocation}
										shouldRefetchLocations={shouldRefetchLocations}
										repeatReloadMapSeconds={repeatReloadMapSeconds}
										currentLocationDrive={currentLocationDrive}
										shouldCheckTime={!driveId}
										handleMouseOutChangeMarkerInfo={handleMouseOutChangeMarkerInfo}
										handleMouseOverChangeMarkerInfo={handleMouseOverChangeMarkerInfo}
									/>
								))}
								{userId &&
									allDriverHeartBeat?.map((heartbeat, index) => (
										<DriverHeartBeatComponent
											key={`heartbeat-${heartbeat.key || index}`}
											data={heartbeat}
											index={index}
											isLastHeartBeat={
												mostRecentHeartBeat
													? heartbeat.key === mostRecentHeartBeat.key
													: index === 0
											}
											handleMouseOutChangeMarkerInfo={handleMouseOutChangeMarkerInfo}
											handleMouseOverChangeMarkerInfo={handleMouseOverChangeMarkerInfo}
										/>
									))}
								{buildMarkerInfoBanner()}
							</>
						)}
					</GoogleMap>
				</Box>
			)}
		</Stack>
	)
}
